import { Box, CardContent, Divider, Stack } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useCallback, useEffect, useState } from 'react';

import { AWSSubnetSelect } from '@/components/regions/awsSubnetSelect';
import { RestoreRegionSelect } from '@/components/regions/restoreRegionSelect';
import { SecurityGroupSelect } from '@/components/regions/securityGroupSelect';
import { VpcSelect } from '@/components/regions/vpcSelect';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import { InstanceTypeSelect } from '../instanceTypeSelection';
import type {
  RestoreBaseState,
  RestoreEc2InstanceState,
} from '../restoreEc2InstanceWizard';

export const InstanceConfiguration = (
  props: StepProps<RestoreEc2InstanceState>
) => {
  const dal = useDAL();
  const getValue = useCallback(
    <T extends keyof RestoreBaseState>(value: T): RestoreBaseState[T] => {
      // keeping undefined for cases where the user selected empty option intentionally.
      return value in props.wizardState.crossStepsState
        ? props.wizardState.crossStepsState[value]
        : props.wizardState.initialState[value];
    },
    [props.wizardState.crossStepsState, props.wizardState.initialState]
  );

  const [scopedState, setScopedState] = useState<RestoreBaseState>({
    regionName: getValue('regionName'),
    AWSSubnetId: getValue('AWSSubnetId'),
    instanceType: getValue('instanceType'),
    securityGroupIds: getValue('securityGroupIds'),
    tags: getValue('tags'),
    keepOriginalTags: getValue('keepOriginalTags'),
  });

  const { data: subnets } = dal.restore.awsSubnets.list(
    props.wizardState.restoreAccount?.id || '',
    scopedState.regionName
  );

  useEffect(() => {
    if (subnets?.subnets) {
      props.setWizardState((state) => {
        const subnet = subnets.subnets.find(
          (subnet) => subnet.id === scopedState.AWSSubnetId
        );
        return {
          ...state,
          vpc: subnet?.vpc || undefined,
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subnets]);

  useEffect(() => {
    setScopedState((state) => ({
      ...state,
      AWSSubnetId: getValue('AWSSubnetId'),
      instanceType: getValue('instanceType'),
      securityGroupIds: getValue('securityGroupIds'),
    }));
  }, [getValue, scopedState.regionName, props.wizardState.vpc]);

  useEffect(() => {
    setScopedState((state) => ({
      ...state,
      instanceType: getValue('instanceType'),
      securityGroupIds: getValue('securityGroupIds'),
    }));
  }, [getValue, scopedState.AWSSubnetId]);

  const onFieldChange = <T extends keyof RestoreBaseState>(
    field: T,
    value: RestoreBaseState[T],
    isUserChange?: boolean
  ) => {
    setScopedState((state) => ({
      ...state,
      [field]: value,
    }));
    if (isUserChange) {
      props.setWizardState((state) => ({
        ...state,
        crossStepsState: { ...state.crossStepsState, [field]: value },
      }));
    }
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      navigationComponent={<EonTagRemark />}
      stepperLabels={props.stepperLabels}
      stepperIndex={1}
      nextButtonText='Next'
      onBackClick={props.back}
      canGoNext={() => {
        if (
          !scopedState.regionName ||
          !scopedState.instanceType ||
          !scopedState.AWSSubnetId
        ) {
          return false;
        }

        return true;
      }}
      onNextClick={async () => {
        props.setWizardState((state) => ({
          ...state,
          crossStepsState: { ...state.crossStepsState, ...scopedState },
        }));
        props.setNextStep(props.currentStep.next?.[0]);
      }}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <Box className='my-[24px] mx-[40px]'>
        <Stack
          direction='row'
          alignItems='center'
          gap='36px'
          className='mt-[20px]'
        >
          <RestoreRegionSelect
            cloudProvider={Provider.Aws}
            restoreAccountId={props.wizardState.restoreAccount?.id}
            regionName={scopedState.regionName}
            onChange={(regionName, isUserChange) =>
              onFieldChange('regionName', regionName, isUserChange)
            }
          />
          <VpcSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={scopedState.regionName}
            vpc={props.wizardState.vpc}
            onChange={(vpc) =>
              props.setWizardState((state) => ({
                ...state,
                vpc: vpc,
              }))
            }
            disabledItems={[]}
          />
        </Stack>
        <Stack
          direction='row'
          alignItems='center'
          gap='36px'
          className='mt-[20px]'
        >
          <AWSSubnetSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={scopedState.regionName}
            subnetId={scopedState.AWSSubnetId}
            vpcScope={props.wizardState.vpc || ''}
            onChange={(AWSSubnetId, isUserChange) =>
              onFieldChange('AWSSubnetId', AWSSubnetId, isUserChange)
            }
          />
          <InstanceTypeSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={scopedState.regionName}
            subnetId={
              props.wizardState.vpc ? scopedState.AWSSubnetId : undefined
            }
            instanceType={scopedState.instanceType}
            onChange={(instanceType, isUserChange) =>
              onFieldChange('instanceType', instanceType, isUserChange)
            }
          />
        </Stack>
        <Stack
          direction='row'
          alignItems='center'
          gap='36px'
          className='mt-[20px]'
        >
          <SecurityGroupSelect
            isMultiSelect={true}
            isOptionalField={true}
            accountId={props.wizardState.restoreAccount?.id}
            regionName={scopedState.regionName}
            securityGroupIds={scopedState.securityGroupIds}
            vpc={props.wizardState.vpc || ''}
            subnetId={scopedState.AWSSubnetId}
            isSubnetIdMandatory={true}
            onChange={(securityGroupIds, event) =>
              onFieldChange('securityGroupIds', securityGroupIds, event)
            }
          />
          <div className='flex-1' />
        </Stack>
      </Box>
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialState.tags || {}}
          tags={scopedState.tags || {}}
          keepOriginalTags={scopedState.keepOriginalTags}
          setKeepOriginalTags={(value) =>
            onFieldChange('keepOriginalTags', value, true)
          }
          onChange={(tags) => onFieldChange('tags', tags, true)}
        />
      </CardContent>
    </StepContainer>
  );
};
